import React, { useState, useEffect, useRef } from 'react';
import { AutoComplete, Input } from 'antd';
import { EnvironmentOutlined } from '@ant-design/icons';
import api from '../services/api';

/**
 * CityAutocomplete component for consistent city name selection.
 * Suggests existing cities from partners and normalizes input.
 */
const CityAutocomplete = ({
  value,
  onChange,
  placeholder = 'Enter city',
  size = 'middle',
  style = {},
  disabled = false,
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allCities, setAllCities] = useState([]);
  const searchTimeoutRef = useRef(null);

  // Fetch all cities on mount
  useEffect(() => {
    fetchAllCities();
  }, []);

  const fetchAllCities = async () => {
    try {
      const response = await api.get('/partners/city-suggestions/');
      setAllCities(response.data.cities || []);
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  const handleSearch = (searchText) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!searchText || searchText.length < 1) {
      // Show all cities when input is empty or has only 1 character
      setOptions(allCities.map(city => ({ value: city, label: city })));
      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      setLoading(true);
      const searchLower = searchText.toLowerCase();
      const filteredCities = allCities.filter(city =>
        city.toLowerCase().includes(searchLower)
      );
      setOptions(filteredCities.map(city => ({
        value: city,
        label: (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <EnvironmentOutlined style={{ marginRight: 8, color: '#1890ff' }} />
            {city}
          </div>
        )
      })));
      setLoading(false);
    }, 150);
  };

  const handleSelect = (selectedValue) => {
    onChange(selectedValue);
  };

  const handleChange = (inputValue) => {
    onChange(inputValue);
  };

  const handleFocus = () => {
    // Show all cities on focus
    setOptions(allCities.map(city => ({
      value: city,
      label: (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <EnvironmentOutlined style={{ marginRight: 8, color: '#1890ff' }} />
          {city}
        </div>
      )
    })));
  };

  return (
    <AutoComplete
      value={value}
      options={options}
      onSearch={handleSearch}
      onSelect={handleSelect}
      onChange={handleChange}
      onFocus={handleFocus}
      placeholder={placeholder}
      style={{ width: '100%', ...style }}
      disabled={disabled}
    >
      <Input
        size={size}
        prefix={<EnvironmentOutlined style={{ color: '#bfbfbf' }} />}
        placeholder={placeholder}
      />
    </AutoComplete>
  );
};

export default CityAutocomplete;
